import { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Download, Trash2, Image, Film, Music, HardDrive,
  CheckSquare, Square, Loader2, RefreshCw, Filter, Eye,
  AlertTriangle, ArrowUpCircle, Scissors, Plus, Share2, Upload, Check, PenLine,
  Search, ChevronLeft, ChevronRight, Scaling,
} from 'lucide-react';
import ImageEditModal from '../imageEdit/ImageEditModal';
import VideoAdaptModal from '../camera/VideoAdaptModal';
import { SocialPublishModal } from '../social';
import { galleryService } from '../../services/galleryService';
import { downloadFile } from '../../utils/downloadFile';
import { sendAssetToTimeline } from '../../utils/sendAssetToTimeline';
import { useWorkflowStore } from '../../store/workflowStore';
import { toast } from 'sonner';
import { brandService } from '../../services/brandService';
import { uploadToR2 } from '../../utils/uploadToR2';
import { describeUploadError, kindOf, limitMB } from '../../utils/uploadLimits';
import { useUploadLimits } from '../../hooks/useUploadLimits';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES, pageCount, pageWindow, rangeLabel } from '../../utils/galleryPaging';

const API = process.env.REACT_APP_BACKEND_URL;

const ASSET_TYPE_CONFIG = {
  all:   { label: 'Todos', icon: Filter },
  image: { label: 'Imagenes', icon: Image },
  video: { label: 'Videos', icon: Film },
  audio: { label: 'Audio', icon: Music },
};

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getAssetName = (asset) => {
  const r2Key = asset.metadata?.r2_key || '';
  const parts = r2Key.split('/');
  return parts[parts.length - 1] || `${asset.asset_type}_${asset.id.slice(0, 8)}`;
};

/**
 * `mode="timeline"` (Envío AP-3): la misma galería abierta desde Edición.
 * «Agregar al timeline» pasa a ser la acción principal, la galería no se
 * cierra al agregar y se puede subir desde la computadora con el tope del plan.
 */
export default function GalleryPanel({ onClose, mode = 'canvas', targetTrack = null }) {
  const timelineMode = mode === 'timeline';
  const [added, setAdded] = useState(() => new Set());
  const [uploadPct, setUploadPct] = useState(null);
  const uploadRef = useRef(null);
  const limits = useUploadLimits();
  const [assets, setAssets] = useState([]);
  const [storage, setStorage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewAsset, setPreviewAsset] = useState(null);
  // AP-4 · «Editar imagen» desde la Galería: guarda una imagen NUEVA.
  const [editSource, setEditSource] = useState(null);
  const [adaptSource, setAdaptSource] = useState(null);   // AP-14 · «Adaptar video»
  const [publishAsset, setPublishAsset] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [total, setTotal] = useState(0);
  // AP-12 · páginas y buscador
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const gridRef = useRef(null);
  const pages = pageCount(total, pageSize);

  const [loadError, setLoadError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Fetch assets and storage independently so one failure doesn't break the other
      try {
        const assetsRes = await galleryService.listAssets({
          assetType: filter, limit: pageSize, offset: (page - 1) * pageSize, q: search,
        });
        setAssets(assetsRes.assets || []);
        setTotal(assetsRes.total || 0);
      } catch (err) {
        console.error('Gallery assets error:', err);
        setAssets([]);
        setTotal(0);
        setLoadError('No se pudieron cargar los archivos. Intenta de nuevo.');
      }

      try {
        const storageRes = await galleryService.getStorageStats();
        setStorage(storageRes);
      } catch (err) {
        console.error('Gallery storage error:', err);
        // Provide default storage info on failure
        setStorage({
          total_mb: 0, total_bytes: 0, total_assets: 0,
          limit_bytes: 500 * 1024 * 1024, limit_mb: 500, limit_label: '500 MB',
          usage_percentage: 0, is_over_limit: false, plan: 'free', plan_name: 'Gratuito',
          breakdown: [],
        });
      }

      setSelected(new Set());
    } catch (err) {
      console.error('Gallery fetch error:', err);
      setLoadError('Error de conexion. Verifica tu conexion y reintenta.');
    } finally {
      setLoading(false);
    }
  }, [filter, page, pageSize, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // buscar mientras se escribe (con una pausa corta) y volver a la página 1
  useEffect(() => {
    const t = setTimeout(() => { setSearch(query.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [query]);
  // si se borraron archivos y la página quedó fuera de rango
  useEffect(() => { if (!loading && page > pages) setPage(pages); }, [loading, page, pages]);
  const goPage = (p) => {
    setPage(Math.min(Math.max(1, p), pages));
    try { gridRef.current?.scrollTo({ top: 0 }); } catch (_e) { /* */ }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === assets.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(assets.map((a) => a.id)));
    }
  };

  // Envío LMX-4c · aviso si la imagen es el logo o una referencia de una marca.
  const brandWarning = async (ids) => {
    const lists = await Promise.all(ids.slice(0, 50).map((id) => brandService.assetUsage(id)));
    const names = [...new Set(lists.flat().map((b) => `${b.name} (${b.uses.join(' y ')})`))];
    return names.length
      ? `\n\nOjo: se usa en la marca ${names.join(', ')}. Si la borrás, la marca pierde esa imagen.`
      : '';
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    const count = selected.size;
    const warn = await brandWarning(Array.from(selected));
    if (!window.confirm(`Eliminar ${count} archivo${count > 1 ? 's' : ''}? Esta accion no se puede deshacer.${warn}`)) return;

    setDeleting(true);
    try {
      await galleryService.bulkDelete(Array.from(selected));
      toast.success(`${count} archivo${count > 1 ? 's' : ''} eliminado${count > 1 ? 's' : ''}`);
      fetchData();
    } catch (err) {
      console.error('Bulk delete error:', err);
      toast.error('Error al eliminar archivos');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDownload = async () => {
    if (selected.size === 0) return;
    setDownloading(true);
    try {
      const res = await galleryService.getBulkDownloadUrls(Array.from(selected));
      const downloads = res.downloads || [];
      for (const dl of downloads) {
        await downloadFile(dl.url, dl.filename);
        await new Promise((r) => setTimeout(r, 400));
      }
      toast.success(`${downloads.length} archivo${downloads.length > 1 ? 's' : ''} descargado${downloads.length > 1 ? 's' : ''}`);
    } catch (err) {
      console.error('Bulk download error:', err);
      toast.error('Error al descargar archivos');
    } finally {
      setDownloading(false);
    }
  };

  const handleSingleDelete = async (asset) => {
    const warn = await brandWarning([asset.id]);
    if (!window.confirm(`Eliminar este archivo?${warn}`)) return;
    try {
      await galleryService.bulkDelete([asset.id]);
      toast.success('Archivo eliminado');
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  // ── Send asset to Timeline Editor ──────────────────────────
  const handleSendToTimeline = useCallback(async (asset) => {
    const res = await sendAssetToTimeline(asset.id, {
      fallbackUrl: asset.url,
      fallbackKind: asset.media_kind || asset.asset_type,
      label: getAssetName(asset),
      duration: asset.duration || asset.metadata?.duration || null,
      thumbUrl: asset.thumbnail_url || asset.metadata?.thumbnail_url || null,
      track: targetTrack === 'v1' || targetTrack === 'v2' ? targetTrack : null,
    });
    if (!timelineMode) { onClose?.(); return; }
    if (res !== null) {
      setAdded((prev) => new Set(prev).add(asset.id));
      toast.success(`${getAssetName(asset)} agregado al timeline`, { position: 'bottom-right' });
    }
  }, [onClose, timelineMode, targetTrack]);

  // AP-3 · subir desde la computadora (con el tope del plan) y, desde Edición, agregarlo.
  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    setUploadPct(0);
    try {
      const up = await uploadToR2(file, { onProgress: setUploadPct, origin: 'gallery' });
      toast.success(`${file.name} subido`, { position: 'bottom-right' });
      await fetchData();
      if (timelineMode && up.assetId) {
        await sendAssetToTimeline(up.assetId, {
          fallbackUrl: up.url, fallbackKind: kindOf(file), label: file.name,
          track: targetTrack === 'v1' || targetTrack === 'v2' ? targetTrack : null,
        });
        setAdded((prev) => new Set(prev).add(up.assetId));
      }
    } catch (err) {
      toast.error(describeUploadError(err), { duration: 8000 });
    } finally {
      setUploadPct(null);
    }
  }, [fetchData, timelineMode, targetTrack]);

  // ── Send asset to Studio canvas ────────────────────────────
  const handleSendToStudio = useCallback((asset) => {
    const wf = useWorkflowStore.getState();
    const kind = asset.asset_type; // 'image' | 'video' | 'audio'
    const nodeType =
      kind === 'image' ? 'uploadImage'
      : kind === 'video' ? 'uploadVideo'
      : 'uploadAudio';
    const fileName = getAssetName(asset);
    wf.addNode(nodeType, undefined, {
      preview: asset.url,
      fileName,
      hasOutput: true,
      outputUrl: asset.url,
      outputType: kind,
      r2Key: asset.metadata?.r2_key || null,
      assetId: asset.id,
      uploaded: true,
      uploading: false,
      fromAsset: true,
    });
    toast.success('Agregado al área de trabajo', { position: 'bottom-right' });
    onClose?.();
  }, [onClose]);

  const handleStorageUpgrade = async () => {
    setUpgrading(true);
    try {
      const res = await galleryService.createStorageUpgrade(window.location.origin);
      if (res.url) {
        window.open(res.url, '_blank');
        setShowUpgradeModal(false);
        toast.success('Redirigiendo al pago...');
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      toast.error('Error al iniciar el upgrade');
    } finally {
      setUpgrading(false);
    }
  };

  // Storage status helpers
  const usagePct = storage?.usage_percentage || 0;
  const isWarning = usagePct >= 80 && usagePct < 95;
  const isCritical = usagePct >= 95;
  const isOverLimit = storage?.is_over_limit;
  const storageBarClass = isCritical ? 'critical' : isWarning ? 'warning' : '';

  useEffect(() => { setPreviewError(false); }, [previewAsset?.id]);

  return (
    <div className="gallery-overlay" data-testid="gallery-panel">
      <div className="gallery-panel">
        {/* Header */}
        <div className="gallery-header">
          <div className="gallery-header-left">
            <HardDrive size={20} />
            <h2>{timelineMode ? 'Galería' : 'Galeria de Medios'}</h2>
            {storage && (
              <span className={`gallery-storage-badge ${storageBarClass}`} data-testid="gallery-storage">
                {storage.total_mb} / {storage.limit_label}
              </span>
            )}
          </div>
          <div className="gallery-upload">
            <button
              type="button"
              className="gallery-upload-btn"
              onClick={() => uploadRef.current?.click()}
              disabled={uploadPct !== null}
              data-testid="gallery-upload"
            >
              <Upload size={15} aria-hidden="true" />
              {uploadPct !== null ? `Subiendo… ${uploadPct} %` : 'Subir desde la computadora'}
            </button>
            {limits && (
              <small>
                Plan {limits.plan_label} · hasta {limitMB(limits, 'video')} MB por video · {limitMB(limits, 'image')} MB por imagen o audio
              </small>
            )}
            <input
              ref={uploadRef}
              type="file"
              hidden
              accept="image/*,video/*,audio/*"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleUpload(f); }}
              data-testid="gallery-upload-input"
            />
          </div>
          <button className="gallery-close-btn" onClick={onClose} data-testid="gallery-close">
            <X size={20} />
          </button>
        </div>

        {/* Storage Bar with Limit */}
        {storage && (
          <div className="gallery-storage-section">
            <div className="gallery-storage-bar-container">
              <div className="gallery-storage-bar">
                <div
                  className={`gallery-storage-fill ${storageBarClass}`}
                  style={{ width: `${Math.min(usagePct, 100)}%` }}
                  data-testid="gallery-storage-fill"
                />
              </div>
              <div className="gallery-storage-pct">
                <span>{usagePct.toFixed(1)}% usado</span>
                <span>Plan {storage.plan_name} ({storage.limit_label})</span>
              </div>
            </div>

            {/* Warning / Over Limit Banner */}
            {(isCritical || isOverLimit) && (
              <div className="gallery-limit-banner critical" data-testid="gallery-limit-banner">
                <AlertTriangle size={16} />
                <span>
                  {isOverLimit
                    ? 'Has alcanzado el limite de almacenamiento.'
                    : 'Estas cerca del limite de almacenamiento.'}
                </span>
                <div className="gallery-limit-actions">
                  <button className="gallery-limit-btn delete" onClick={() => { toggleSelectAll(); }}>
                    <Trash2 size={13} /> Seleccionar todo para borrar
                  </button>
                  {storage.plan === 'free' && (
                    <button
                      className="gallery-limit-btn upgrade"
                      onClick={() => setShowUpgradeModal(true)}
                      data-testid="gallery-upgrade-btn"
                    >
                      <ArrowUpCircle size={13} /> Upgrade a Pro (5 GB)
                    </button>
                  )}
                </div>
              </div>
            )}

            {isWarning && !isCritical && (
              <div className="gallery-limit-banner warning">
                <AlertTriangle size={14} />
                <span>80% del almacenamiento usado. Considera liberar espacio o hacer upgrade.</span>
                {storage.plan === 'free' && (
                  <button
                    className="gallery-limit-btn upgrade small"
                    onClick={() => setShowUpgradeModal(true)}
                  >
                    <ArrowUpCircle size={12} /> Upgrade
                  </button>
                )}
              </div>
            )}

            <div className="gallery-storage-legend">
              {storage.breakdown?.map((b) => {
                const Icon = ASSET_TYPE_CONFIG[b.type]?.icon || HardDrive;
                return (
                  <span key={b.type} className="gallery-legend-item">
                    <Icon size={12} />
                    <span>{ASSET_TYPE_CONFIG[b.type]?.label || b.type}: {b.count} ({b.size_mb} MB)</span>
                  </span>
                );
              })}
              <span className="gallery-legend-total">
                Total: {storage.total_assets} archivos / {storage.total_mb} MB
              </span>
            </div>
          </div>
        )}

        {/* Filters + Actions */}
        <div className="gallery-toolbar">
          <div className="gallery-filters">
            {Object.entries(ASSET_TYPE_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={key}
                  className={`gallery-filter-btn ${filter === key ? 'active' : ''}`}
                  onClick={() => { setFilter(key); setPage(1); }}
                  data-testid={`gallery-filter-${key}`}
                >
                  <Icon size={14} /> {cfg.label}
                </button>
              );
            })}
          </div>
          <label className="gallery-search">
            <Search size={14} aria-hidden="true" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre…" aria-label="Buscar en la Galería" data-testid="gallery-search" />
            {query && (
              <button type="button" className="gallery-search-clear" onClick={() => setQuery('')} aria-label="Borrar búsqueda">
                <X size={12} />
              </button>
            )}
          </label>
          <div className="gallery-actions">
            <button className="gallery-action-btn" onClick={toggleSelectAll} data-testid="gallery-select-all">
              {selected.size === assets.length && assets.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
              {selected.size > 0 ? `${selected.size} sel.` : 'Seleccionar'}
            </button>
            {selected.size > 0 && (
              <>
                <button
                  className="gallery-action-btn download"
                  onClick={handleBulkDownload}
                  disabled={downloading}
                  data-testid="gallery-bulk-download"
                >
                  {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  Descargar ({selected.size})
                </button>
                <button
                  className="gallery-action-btn delete"
                  onClick={handleBulkDelete}
                  disabled={deleting}
                  data-testid="gallery-bulk-delete"
                >
                  {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Eliminar ({selected.size})
                </button>
              </>
            )}
            <button className="gallery-action-btn refresh" onClick={fetchData} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Grid */}
        <div className="gallery-grid-container" ref={gridRef}>
          {loading ? (
            <div className="gallery-loading">
              <Loader2 size={32} className="animate-spin" />
              <span>Cargando galeria...</span>
            </div>
          ) : loadError ? (
            <div className="gallery-error" data-testid="gallery-error">
              <AlertTriangle size={48} />
              <p>{loadError}</p>
              <button className="gallery-retry-btn" onClick={fetchData}>
                <RefreshCw size={14} /> Reintentar
              </button>
            </div>
          ) : assets.length === 0 ? (
            <div className="gallery-empty">
              <HardDrive size={48} />
              <p>{search
                ? `No hay archivos que coincidan con «${search}»`
                : `No hay archivos ${filter !== 'all' ? `de tipo ${ASSET_TYPE_CONFIG[filter]?.label}` : ''}`}</p>
            </div>
          ) : (
            <div className="gallery-grid" data-testid="gallery-grid">
              {assets.map((asset) => (
                <GalleryItem
                  key={asset.id}
                  asset={asset}
                  isSelected={selected.has(asset.id)}
                  onToggle={() => toggleSelect(asset.id)}
                  onPreview={() => setPreviewAsset(asset)}
                  onDelete={() => handleSingleDelete(asset)}
                  onSendToTimeline={() => handleSendToTimeline(asset)}
                  onSendToStudio={() => handleSendToStudio(asset)}
                  onPublish={() => setPublishAsset(asset)}
                  onEdit={() => setEditSource({
                    url: asset.url, assetId: asset.id,
                    label: String(asset.filename || getAssetName(asset)).replace(/^[0-9a-f]{8,}[-_]/i, ''),
                  })}
                  onAdapt={() => setAdaptSource({ url: asset.url, label: getAssetName(asset) })}
                  timelineMode={timelineMode}
                  added={added.has(asset.id)}
                />
              ))}
            </div>
          )}
          {!loading && !loadError && total > 0 && (
            <nav className="gallery-pager" aria-label="Páginas de la Galería" data-testid="gallery-pager">
              <span className="gallery-pager-range" data-testid="gallery-range">{rangeLabel(page, pageSize, total)}</span>
              <div className="gallery-pager-pages">
                <button type="button" onClick={() => goPage(page - 1)} disabled={page <= 1} aria-label="Página anterior" data-testid="gallery-prev">
                  <ChevronLeft size={14} />
                </button>
                {pageWindow(page, pages).map((p, i) => (p == null
                  ? <span key={`gap-${i}`} className="gallery-pager-gap">…</span>
                  : (
                    <button type="button" key={p} onClick={() => goPage(p)} aria-current={p === page ? 'page' : undefined}
                      className={p === page ? 'is-on' : ''} data-testid={`gallery-page-${p}`}>{p}</button>
                  )))}
                <button type="button" onClick={() => goPage(page + 1)} disabled={page >= pages} aria-label="Página siguiente" data-testid="gallery-next">
                  <ChevronRight size={14} />
                </button>
              </div>
              <label className="gallery-pager-size">
                <span>Por página</span>
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} data-testid="gallery-page-size">
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            </nav>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewAsset && (
        <div className="gallery-preview-overlay" onClick={() => setPreviewAsset(null)}>
          <div className="gallery-preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="gallery-preview-close" onClick={() => setPreviewAsset(null)}><X size={20} /></button>
            {previewAsset.asset_type === 'image' && (
              <img
                src={previewAsset.url}
                alt="Preview"
                onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement('div'), { className: 'gallery-preview-broken', textContent: 'No se pudo cargar la imagen' })); }}
              />
            )}
            {previewAsset.asset_type === 'video' && (
              <video
                src={previewAsset.url}
                controls
                autoPlay
                onError={() => toast.error('No se pudo cargar el video')}
              />
            )}
            {previewAsset.asset_type === 'audio' && (
              <div className="gallery-preview-audio">
                <Music size={64} />
                <audio
                  src={previewAsset.url}
                  controls
                  preload="auto"
                  onError={() => toast.error('No se pudo cargar el audio')}
                />
              </div>
            )}
            <div className="gallery-preview-info">
              <span>{getAssetName(previewAsset)}</span>
              <span>{formatDate(previewAsset.created_at)}</span>
              <button className="gallery-preview-download" onClick={() => downloadFile(previewAsset.url, getAssetName(previewAsset))}>
                <Download size={14} /> Descargar
              </button>
              {['image', 'video'].includes(previewAsset.asset_type) && (
                <button
                  className="gallery-preview-download"
                  onClick={() => { setPublishAsset(previewAsset); setPreviewAsset(null); }}
                  data-testid="gallery-preview-publish"
                >
                  <Share2 size={14} /> Publicar en redes
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (        <div className="gallery-preview-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="gallery-upgrade-modal" onClick={(e) => e.stopPropagation()} data-testid="gallery-upgrade-modal">
            <button className="gallery-preview-close" onClick={() => setShowUpgradeModal(false)}><X size={20} /></button>
            <div className="gallery-upgrade-icon">
              <ArrowUpCircle size={48} />
            </div>
            <h3>Upgrade de Almacenamiento</h3>
            <p className="gallery-upgrade-desc">
              Tu plan actual es <strong>Gratuito (500 MB)</strong>. 
              Actualiza a <strong>Pro</strong> para obtener <strong>5 GB</strong> de almacenamiento.
            </p>
            <div className="gallery-upgrade-comparison">
              <div className="gallery-plan-card current">
                <span className="gallery-plan-badge">Actual</span>
                <h4>Gratuito</h4>
                <span className="gallery-plan-limit">500 MB</span>
                <span className="gallery-plan-price">$0</span>
              </div>
              <div className="gallery-plan-arrow">→</div>
              <div className="gallery-plan-card upgrade">
                <span className="gallery-plan-badge pro">Recomendado</span>
                <h4>Pro</h4>
                <span className="gallery-plan-limit">5 GB</span>
                <span className="gallery-plan-price">$9.99</span>
              </div>
            </div>
            <div className="gallery-upgrade-actions">
              <button
                className="gallery-upgrade-buy-btn"
                onClick={handleStorageUpgrade}
                disabled={upgrading}
                data-testid="gallery-upgrade-buy"
              >
                {upgrading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpCircle size={16} />}
                Upgrade a Pro — $9.99
              </button>
              <button className="gallery-upgrade-cancel" onClick={() => setShowUpgradeModal(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publicar en redes */}
      {publishAsset && (
        <SocialPublishModal asset={publishAsset} onClose={() => setPublishAsset(null)} />
      )}
      {adaptSource && (
        <VideoAdaptModal source={adaptSource} onClose={() => setAdaptSource(null)} onDone={() => fetchData()} />
      )}
      {editSource && (
        <ImageEditModal
          source={editSource}
          canvasOption={!timelineMode}
          onClose={() => setEditSource(null)}
          onSaved={() => fetchData()}
        />
      )}
    </div>
  );
}

function GalleryItem({ asset, isSelected, onToggle, onPreview, onDelete, onSendToTimeline, onSendToStudio, onPublish, onEdit, onAdapt, timelineMode = false, added = false }) {
  const TypeIcon = ASSET_TYPE_CONFIG[asset.asset_type]?.icon || HardDrive;
  const name = getAssetName(asset);
  const canPublish = ['image', 'video'].includes(asset.asset_type) && !!asset.url;

  return (
    <div className={`gallery-item ${isSelected ? 'selected' : ''}`} data-testid={`gallery-item-${asset.id}`}>
      <button className="gallery-item-select" onClick={onToggle}>
        {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
      </button>

      <div className="gallery-item-thumb" onClick={onPreview}>
        {asset.asset_type === 'image' && (
          <img
            src={asset.url}
            alt={name}
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement?.classList.add('broken'); }}
          />
        )}
        {asset.asset_type === 'video' && (
          <div className="gallery-item-video-thumb">
            {asset.metadata?.thumbnail_url ? (
              <img src={asset.metadata.thumbnail_url} alt={name} loading="lazy" />
            ) : (
              <video src={asset.url} preload="none" muted />
            )}
            <Film size={24} className="gallery-video-icon" />
          </div>
        )}
        {asset.asset_type === 'audio' && (
          <div className="gallery-item-audio-thumb">
            <Music size={32} />
          </div>
        )}
      </div>

      <div className="gallery-item-info">
        <span className="gallery-item-name" title={name}>{name}</span>
        <div className="gallery-item-meta">
          <TypeIcon size={10} />
          <span>{formatDate(asset.created_at)}</span>
        </div>
      </div>

      {timelineMode && (
        <button
          type="button"
          className={`gallery-item-add ${added ? 'is-added' : ''}`}
          onClick={onSendToTimeline}
          data-testid={`gallery-add-timeline-${asset.id}`}
        >
          {added ? <Check size={14} aria-hidden="true" /> : <Plus size={14} aria-hidden="true" />}
          {added ? 'Agregado · otra vez' : 'Agregar al timeline'}
        </button>
      )}
      <div className="gallery-item-actions">
        <button
          className="gallery-item-btn studio"
          onClick={onSendToStudio}
          title="Agregar al área de trabajo"
          data-testid={`gallery-send-studio-${asset.id}`}
        >
          <Plus size={12} />
        </button>
        <button
          className="gallery-item-btn timeline"
          onClick={onSendToTimeline}
          title="Agregar al Timeline"
          data-testid={`gallery-send-timeline-${asset.id}`}
        >
          <Scissors size={12} />
        </button>
        {asset.asset_type === 'image' && !!asset.url && onEdit && (
          <button
            className="gallery-item-btn edit"
            onClick={onEdit}
            title="Editar imagen (guarda una copia nueva)"
            data-testid={`gallery-edit-${asset.id}`}
          >
            <PenLine size={12} />
          </button>
        )}
        {asset.asset_type === 'video' && !!asset.url && onAdapt && (
          <button
            className="gallery-item-btn edit"
            onClick={onAdapt}
            title="Adaptar video: recortar, resolución, peso (guarda una copia nueva)"
            data-testid={`gallery-adapt-${asset.id}`}
          >
            <Scaling size={12} />
          </button>
        )}
        <button className="gallery-item-btn preview" onClick={onPreview} title="Ver">
          <Eye size={12} />
        </button>
        {canPublish && (
          <button
            className="gallery-item-btn publish"
            onClick={onPublish}
            title="Publicar en redes"
            data-testid={`gallery-publish-${asset.id}`}
          >
            <Share2 size={12} />
          </button>
        )}
        <button className="gallery-item-btn download" onClick={() => downloadFile(asset.url, name)} title="Descargar">
          <Download size={12} />
        </button>
        <button className="gallery-item-btn delete" onClick={onDelete} title="Eliminar">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

GalleryPanel.displayName = 'GalleryPanel';
